import { Inject, Injectable, Injector, isDevMode, OnDestroy, VERSION as AngularVersion } from '@angular/core';
import { ActivatedRoute, UrlSegment } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  empty,
  merge as rxMerge,
  MonoTypeOperatorFunction,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { catchError, distinctUntilChanged, map, merge, share, startWith, subscribeOn, switchMap, tap } from 'rxjs/operators';

import {
  CYCLE_HANDLING,
  HttpResourceOptions,
  PageSearchResult,
  RenderingContext,
  RenderingContextQueryResult,
  RenderingContextSearchResult,
  SearchResult,
  SearchResults,
  Site,
  SiteContext,
  SitePage,
  SitePagesQueryResult,
} from './../../api';
import { WCH_RC_INTERCEPTOR_TOKEN } from './../../ng-api';
import {
  AnyJson,
  anyToString,
  arrayPush,
  cloneURL,
  constGenerator,
  createCache,
  dbgCounter,
  DEFAULT_HTTP_RESOURCE_OPTIONS,
  EMPTY_JSON_OBSERVABLE,
  EMPTY_RENDERING_CONTEXT,
  filterTypeOf,
  freezeDeep,
  generateItem,
  Generator,
  httpCreateTimer,
  httpGetJsonResource,
  httpGetStringResource,
  HttpService,
  idleFrameScheduler,
  isAbsoluteURL,
  isNil,
  isNotNil,
  isStringArray,
  jsonParse,
  KEY_CYCLE,
  KEY_RENDERING_CONTEXT_MAP,
  opDeepDistinctUntilChanged,
  opDistinctUntilChanged,
  opFilterNotNil,
  opShareLast,
  pageCloneSitePage,
  pageCreatePartialSiteContextForSitePage,
  pageCreateSiteContextFromSearchResult,
  pageEquals,
  parseURL,
  pathForSearch,
  perfMeasure,
  RenderingContextInterceptors,
  RenderingContextMap,
  rxNext,
  rxPipe,
  safeUnsubscribe,
  siteContextEquals,
  SiteInformation,
  StaticResources,
  thisThenThats,
  unary,
  urlToString,
  wchAddDebug,
  wchAddTypings,
  wchAdjustRenderingContextSearchQuery,
  wchAdjustSitePagesSearchQuery,
  wchBoxFetchLevels,
  wchCycleHandling,
  wchForEachRenderingContext,
  wchGetPageSearchURL,
  wchGetParentPageURL,
  wchGetSearchURL,
  wchGetSiteContextURL,
  wchIsPreviewMode,
  wchPrepareRenderingContextInterceptors,
  wchResolveRenderingContext,
} from './../../utils';
import { HubContext } from './../interfaces/hub-context';
import { assertDependency, DEV_MODE_SUBJECT, isAssertOn } from './../internal/assert';
import { HTTP_SERVICE_ON_HTTP_CLIENT } from './../utils/http.service.on.http.client';
import { HTTP_SERVICE_ON_JSONP } from './../utils/http.service.on.jsonp';
import { assign, cloneDeep, deepEquals } from './../utils/js.utils';
import { lazyInjector } from './../utils/lazy.injector';
import { markupAddMarkup, markupDefineAccessors, markupEmptyMarkupMapping, MarkupMapping } from './../utils/markup.utils';
import { pathGetPathFromUrlSegments } from './../utils/site.utils';
import { BFF_BASE_URL, USE_PUBLIC_URL } from './../utils/urls';
import { wchGetRenderingContextURL, wchGetSiteURL } from './../utils/wch.utils';
import { VERSION as SdkVersion } from './../version';
import { BootstrapService } from './bootstrap/bootstrap.service';
import { ApplicationConfig } from './config/application.config';
import { EMPTY_APPLICATION_CONFIG } from './config/empty.application.config';
import { HubInfoService } from './hub-info/hub-info.service';
import { li, lid } from './logger/logger.service';
import { WchLoggerService } from './logger/wch.logger.service';
import { MarkupGenerator, MarkupProvider, MarkupProviders, MarkupService } from './markup/markup.service';
import { StorageService } from './storage/storage.service';
import { UrlsService } from './url/urls.service';
import { opAssertInAngularZone, ZoneService } from './zone/zone.service';

/* Copyright IBM Corp. 2017 */
/* import {
  HandlebarsMarkup, HandlebarsMarkupMapping, HandlebarsTemplate, hbsAddHandlebarsMarkup,
  hbsAugmentTemplate, hbsCompile, hbsDefineAccessors, hbsEmptyHandlebarsMarkupMapping
} from './../utils/handlebars.utils'; */
const _BehaviorSubject = BehaviorSubject;

const EMPTY_RENDERING_CONTEXT_SEARCH_RESULT: RenderingContextSearchResult[] = [];
const EMPTY_PAGE_SEARCH_RESULT: PageSearchResult[] = [];
const EMPTY_RENDERING_CONTEXTS: RenderingContext[] = [];
const EMPTY_RENDERING_QUERY: RenderingContextQueryResult = {
  numFound: 0,
  renderingContexts: EMPTY_RENDERING_CONTEXTS
};
const EMPTY_SITE_PAGES_QUERY: SitePagesQueryResult = {
  numFound: 0,
  sitePages: []
};
/*
 * If the search string is empty, we produce an empty search result instead of no search result
 */
const EMPTY_RENDERING_CONTEXTS_SEQUENCE: Observable<
  RenderingContextQueryResult
> = of(EMPTY_RENDERING_QUERY);
const EMPTY_SITE_PAGES_SEQUENCE: Observable<SitePagesQueryResult> = of(
  EMPTY_SITE_PAGES_QUERY
);

const NO_RENDERING_CONTEXT: Observable<RenderingContext> = of(
  EMPTY_RENDERING_CONTEXT
).pipe(map(cloneDeep));
const UNKNOWN_RENDERING_CONTEXT: Observable<RenderingContext> = of(undefined);

const LOGGER = 'WchService';
const _Subject = Subject;

// per default break cycles
const DEFAULT_CYCLE_HANDLING = CYCLE_HANDLING.BREAK;

/**
 * Extracts the document element from a search result
 *
 * @param aSearchResult   the result
 * @return the document
 */
function _pluckDocument<T>(aSearchResult: SearchResult<T>): T {
  return aSearchResult.document;
}

/**
 * Extracts the site ID from the site
 *
 * @param aSite the site
 * @return the document
 */
function _pluckSiteId(aSite: Site): string {
  return aSite.id;
}

@Injectable()
export class WchService implements OnDestroy {
  // override OnDestroy
  ngOnDestroy: () => void;

  // public methods
  triggerRefresh: () => void;
  getApiUrl: () => URL;
  getDeliveryUrl: () => URL;
  getBaseUrl: () => URL | undefined;
  isPreviewMode: () => boolean;
  getDeliverOrigin: () => string;
  getAppConfig: () => Observable<ApplicationConfig>;
  getRenderingContextById: (
    aID: string | null | undefined,
    aLevels?: number
  ) => Observable<RenderingContext | null | undefined>;
  getRenderingContextByUrlSegments: (
    aSegments: UrlSegment[],
    aLevels?: number
  ) => Observable<RenderingContext | null | undefined>;
  getRenderingContextByPath: (
    aPath: string,
    aLevels?: number
  ) => Observable<RenderingContext | null | undefined>;
  getRenderingContextByPage: (
    aPage: SitePage,
    aLevels?: number
  ) => Observable<RenderingContext | null | undefined>;
  getRenderingContextByActivatedRoute: (
    aRoute: ActivatedRoute,
    aLevels?: number
  ) => Observable<RenderingContext | null | undefined>;
  getRenderingContextForAppConfig: (
    aLevels?: number
  ) => Observable<RenderingContext>;
  getRenderingContextsByQuery: (
    aQuery: string,
    aLevels?: number
  ) => Observable<RenderingContextQueryResult>;
  getSitePagesByQuery: (aQuery: string) => Observable<SitePagesQueryResult>;
  getCorsWhitelist: () => Observable<string[]>;
  getJsonResource: <T>(
    aUrl: string,
    aOptions?: HttpResourceOptions
  ) => Observable<T>;
  getTextResource: (
    aUrl: string,
    aOptions?: HttpResourceOptions
  ) => Observable<string>;
  resolveRenderingContext: (
    aRenderingContext?: RenderingContext,
    aStrategy?: CYCLE_HANDLING
  ) => Observable<RenderingContext | null | undefined>;

  // protected methods
  protected createPollingTrigger: (
    aOptions?: HttpResourceOptions
  ) => Observable<any>;

  constructor(
    @Inject(HTTP_SERVICE_ON_HTTP_CLIENT) aHttp: HttpService,
    @Inject(HTTP_SERVICE_ON_JSONP) aJsonp: HttpService,
    @Inject(HubInfoService) wchConfig: HubInfoService,
    aUrlService: UrlsService,
    clientStorage: StorageService,
    bootstrapService: BootstrapService,
    markupProviderService: MarkupService,
    zoneService: ZoneService,
    loggerService: WchLoggerService,
    aInjector: Injector
  ) {
    // measure
    const perfConstructor = perfMeasure(LOGGER);

    // validate the dependencies
    if (isAssertOn()) {
      // assert our dependencies
      assertDependency(aHttp, 'Http');
      assertDependency(aJsonp, 'Jsonp');
      assertDependency(aUrlService, 'UrlsService');
      assertDependency(wchConfig, 'HubInfoService');
      assertDependency(clientStorage, 'StorageService');
      assertDependency(bootstrapService, 'BootstrapService');
      assertDependency(markupProviderService, 'MarkupService');
    }

    // log the version
    const _devMode = isDevMode();
    li(
      LOGGER,
      'Sdk',
      SdkVersion,
      'Angular',
      AngularVersion.full,
      'DevMode',
      _devMode
    );

    // update the mode
    DEV_MODE_SUBJECT.next(_devMode);

    // access to the instance
    const that = this;

    // access the logger
    const _logger = loggerService.get(LOGGER);
    const log: { <T>(value: string): MonoTypeOperatorFunction<T> } = rxNext(
      _logger
    );

    // log this
    lid(LOGGER, 'WchConfig', wchConfig);

    const _apiUrl = parseURL(aUrlService.apiUrl);
    const _deliveryUrl = parseURL(aUrlService.deliveryUrl);
    const _deliverOrigin = _deliveryUrl.origin;
    const _isPreviewMode = wchIsPreviewMode(_apiUrl);
    const _apiRoot = urlToString(_apiUrl);

    // prepare the interceptors, but resolve lazily
    const _interceptors: Generator<RenderingContextInterceptors> = lazyInjector(
      aInjector,
      WCH_RC_INTERCEPTOR_TOKEN,
      [],
      wchPrepareRenderingContextInterceptors
    );

    // check if we run with credentials
    const _onWithCredentials = rxPipe(
      USE_PUBLIC_URL,
      map(bPublic => _isPreviewMode || !bPublic),
      opDistinctUntilChanged
    );

    // cycle handling strategy
    const _cycleHandlingStrategy: CYCLE_HANDLING =
      wchCycleHandling(wchConfig.cycleHandlingStrategy) ||
      DEFAULT_CYCLE_HANDLING;
    lid(LOGGER, '_cycleHandlingStrategy', _cycleHandlingStrategy);

    // levels
    const _fetchLevels = wchBoxFetchLevels(wchConfig.fetchLevels);
    lid(LOGGER, '_fetchLevels', _fetchLevels);

    // access to static resources as a fallback
    const _staticResources = new StaticResources(_deliveryUrl, aHttp);

    // extract the options
    const _httpResourceOptions = assign(
      {},
      DEFAULT_HTTP_RESOURCE_OPTIONS,
      wchConfig.httpOptions,
      _isPreviewMode ? wchConfig.httpPreviewOptions : undefined
    );

    lid(LOGGER, 'HttpResourceOptions', _httpResourceOptions);

    // detect the http service to use
    const _http: HttpService = _httpResourceOptions.useJsonP ? aJsonp : aHttp;

    const _siteInfo = new SiteInformation();

    const _appConfig = new _BehaviorSubject<any>(EMPTY_APPLICATION_CONFIG);

    /*
     * The list of compiled templates based on URL
     */
    const _compiledTemplatesAsync = createCache<Observable<MarkupGenerator>>();

    // the hub context so we can inject it
    const _hubContext: HubContext = {
      apiUrl: cloneURL(_apiUrl),
      deliveryUrl: cloneURL(_deliveryUrl),
      isPreviewMode: _isPreviewMode
    };

    // the trigger
    const _trigger = new _Subject<any>();

    // init the trigger
    const _updateObserver = rxPipe(_trigger, share());
    const _updateTrigger = _trigger;

    let _siteSubscription: Subscription;

    /*
   * Loads a template from a URL and compiles it
   *
   * @param aURL  the URL
   * @param aMarkupProvider the actual generator of the markup
   * @param aOptions  the request options
   */
    function _compileAsync(
      aURL: string,
      aMarkupProvider: MarkupProvider,
      aOptions?: HttpResourceOptions
    ): Observable<MarkupGenerator> {
      // options to use
      const options = aOptions || _httpResourceOptions;
      // the cache
      return _compiledTemplatesAsync(aURL, key =>
        _getStringResource(key, options).pipe(
          switchMap<string, MarkupGenerator>(template =>
            aMarkupProvider.compile(aURL, template)
          ),
          // cache the last value
          opShareLast
        )
      );
    }

    /*
     * Constructs a refresh trigger for polling operations
     *
     * @param aOptions  the configuration
     * @return the trigger that fired an event whenever polling is supposed to occur
     */
    function _createPollingTrigger(
      aOptions?: HttpResourceOptions
    ): Observable<any> {
      // options to use
      const options = aOptions || _httpResourceOptions;
      return httpCreateTimer(options);
    }

    /*
     * Fetches the resources from the delivery server
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable of the data with its priority
     */
    function _getStaticResource(
      aUrl: string,
      aOptions: HttpResourceOptions
    ): Observable<AnyJson> {
      // check
      if (aOptions.useStaticResources) {
        // return from the service
        return rxPipe(
          // read the static resource
          _staticResources.get(aUrl),
          // no empty results
          opFilterNotNil
        );
      }
      // nothing
      return EMPTY_JSON_OBSERVABLE;
    }

    /*
     * Fetches the resource via local storage if available
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable of the data with its priority
     */
    function _getLocalStorageResource(
      aUrl: string,
      aOptions: HttpResourceOptions
    ): Observable<AnyJson> {
      // check
      if (aOptions.useLocalStorage) {
        // return from the service
        return rxPipe(
          // load from client storage
          generateItem<AnyJson>(() => clientStorage.get(aUrl)),
          // execute later
          subscribeOn(idleFrameScheduler),
          // ignore empty results
          opFilterNotNil,
          // ignore potential errors
          catchError(err => EMPTY_JSON_OBSERVABLE)
        );
      }
      // nothing
      return EMPTY_JSON_OBSERVABLE;
    }

    /*
     * Fetches the resource via bootstrap service  if available
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable of the data with its priority
     */
    function _getBootstrappedResource(
      aUrl: string,
      aOptions: HttpResourceOptions
    ): Observable<AnyJson> {
      // check
      if (aOptions.useBootstrap) {
        // return from the service
        return rxPipe(
          // from bootstrap
          bootstrapService.get(aUrl),
          // execute later
          subscribeOn(idleFrameScheduler),
          // ignore empty results
          opFilterNotNil,
          // ignore potential errors
          catchError(err => EMPTY_JSON_OBSERVABLE)
        );
      }
      // nothing
      return EMPTY_JSON_OBSERVABLE;
    }

    /*
     * Fetches the resource via the standard HTTP way
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable
     */
    function _getJsonHttpResource(
      aUrl: string,
      aOptions?: HttpResourceOptions
    ): Observable<AnyJson> {
      // construct our trigger
      const options: HttpResourceOptions = aOptions || _httpResourceOptions;
      const trigger = rxMerge(
        _updateObserver,
        that.createPollingTrigger(options)
      );
      // select the http driver
      const http: HttpService = options.useJsonP ? aJsonp : aHttp;
      // the URL
      const url = isAbsoluteURL(aUrl) ? aUrl : `${_apiRoot}${aUrl}`;
      // access the cached version
      const resp = rxPipe(
        _onWithCredentials,
        switchMap<boolean, AnyJson>(bWithCredentials =>
          httpGetJsonResource(
            url,
            http,
            bWithCredentials,
            trigger,
            aOptions,
            _logger
          )
        )
      );
      // optionally write the result to local storage
      if (aOptions.useLocalStorage) {
        // writing the value to our local cache
        return rxPipe(resp, tap(data => clientStorage.put(aUrl, data)));
      }
      // just return as is
      return resp;
    }

    /*
     * Fetches the resource via the standard HTTP way if configured
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable
     */
    function _getApiResource(
      aUrl: string,
      aOptions: HttpResourceOptions
    ): Observable<AnyJson> {
      // check if we are supposed to use the API
      return aOptions.useApi
        ? _getJsonHttpResource(aUrl, aOptions)
        : EMPTY_JSON_OBSERVABLE;
    }

    /*
     * Fetches the resource via the standard HTTP way
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable
     */
    function _getStringResource(
      aUrl: string,
      aOptions?: HttpResourceOptions
    ): Observable<string> {
      // construct our trigger
      const trigger = rxMerge(
        _updateObserver,
        that.createPollingTrigger(aOptions)
      );
      // the URL
      const url = isAbsoluteURL(aUrl) ? aUrl : `${_apiRoot}${aUrl}`;
      // access the cached version
      return rxPipe(
        _onWithCredentials,
        switchMap<boolean, string>(bWithCredentials =>
          httpGetStringResource(
            url,
            aHttp,
            bWithCredentials,
            trigger,
            aOptions,
            _logger
          )
        )
      );
    }

    /**
     * Returns the search result documents
     *
     * @param aUrl  the URL
     * @param aOptions  the options
     */
    function _getSearchDocuments<T>(
      aUrl: string,
      aOptions?: HttpResourceOptions
    ): Observable<T[]> {
      // execute the search
      return rxPipe(
        _getStringResource(aUrl, aOptions),
        map<string, SearchResults<SearchResult<T>>>(jsonParse),
        map<SearchResults<SearchResult<T>>, SearchResult<T>[]>(
          searchResult => searchResult.documents || []
        ),
        map<SearchResult<T>[], T[]>(searchResults =>
          searchResults.map(_pluckDocument)
        )
      );
    }

    /*
     * Fetches the resource via the configured HTTP way
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     *
     * @return observable
     */
    function _getJsonResource<T>(
      aUrl: string,
      aOptions?: HttpResourceOptions
    ): Observable<T> {
      // start measuring
      const id = dbgCounter();
      // options to use
      const options: HttpResourceOptions = aOptions
        ? Object.assign({}, _httpResourceOptions, aOptions)
        : _httpResourceOptions;
      // assemble the potential sources
      const fromLocalStorage = _getLocalStorageResource(aUrl, options),
        fromBootstrap = _getBootstrappedResource(aUrl, options),
        fromStatic = _getStaticResource(aUrl, options),
        fromApi = _getApiResource(aUrl, options);
      // merge the streams
      return thisThenThats(
        fromBootstrap,
        fromStatic,
        fromLocalStorage,
        fromApi
      ).pipe(
        // compare semantically to avoid changes
        distinctUntilChanged(deepEquals),
        // make sure we run on a zone
        zoneService.observeInside()
      );
    }

    /*
     * Clones the given context and injects the site context and the result of the handlebars compilation
     *
     * @param aContext    the rendering context to clone
     * @param aMappings   the handlebars mappings
     * @param aSiteInfo   site infor to resolve the site context
     * @param aOptions    request options
     */
    function _cloneRenderingContext(
      aRenderingContext: RenderingContext,
      aMappings: MarkupMapping,
      aSiteContext: SiteContext,
      aOptions?: HttpResourceOptions
    ): RenderingContext {
      // make a copy of the context, so we don't mess with shared references
      const copiedContext = cloneDeep(aRenderingContext);
      // maintain a list of IDs to resolve references
      const allContexts: RenderingContextMap = {};
      // work on the copy and inject the desired data into each sub-context
      wchForEachRenderingContext(copiedContext, (rc, parent, path) => {
        // the id
        const rcId = anyToString(rc.id);
        // update the site context
        (rc as any).context = {
          ...aSiteContext,
          hub: _hubContext
        };
        // update the markup templates
        markupDefineAccessors(rc, aMappings.byId[rcId], aOptions);
        // add the typings
        wchAddTypings(rc);
        // add debug info
        wchAddDebug(path, rc);
        // only add if parent is not a cycle
        if (isNotNil(parent) && isNil(parent[KEY_CYCLE])) {
          allContexts[rcId] = rc;
        }
        // add the global resolution map
        rc[KEY_RENDERING_CONTEXT_MAP] = allContexts;
      });
      // returns the copied context
      return copiedContext;
    }

    /*
     * Assembles the handlebars template references and makes sure that the markup
     * context gets updated (rather invalidated) whenever the template changes.
     *
     * @param aMappings the mappings
     * @param aOptions  the request options
     */
    function _createTemplateRefs(
      aMappings: MarkupMapping,
      aOptions?: HttpResourceOptions
    ): Observable<string>[] {
      // listen for changes to the template files
      const refs: Observable<string>[] = [];
      // use the ordering by template, so we load the template once and then update all contexts
      const byTemplate = aMappings.byTemplate;
      // tslint:disable-next-line:forin
      for (const templateRef in byTemplate) {
        // markups ordered by providers
        const hbsMarkupRefs = byTemplate[templateRef];
        // handle the markups by provider
        let lenRefs = hbsMarkupRefs.length;
        while (lenRefs-- > 0) {
          // markup references
          const hbsMarkupRef = hbsMarkupRefs[lenRefs];
          const provider = hbsMarkupRef.provider;
          const markupRef = hbsMarkupRef.markupRef;
          arrayPush(
            _compileAsync(templateRef, provider, aOptions).pipe(
              // update the templates
              map<MarkupGenerator, string>(tmp => {
                // replace the markup
                markupRef.template = tmp;
                markupRef.markup = undefined;
                // reduce to the template reference for easier logging
                return templateRef;
              })
            ),
            refs
          );
        }
      }
      // ok
      return refs;
    }

    function _createForSite(
      aSiteContext: SiteContext,
      aRenderingContext: RenderingContext,
      aProviders: MarkupProviders,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext> {
      // options to use
      const options = aOptions || _httpResourceOptions;
      // remember which site contexts to update
      const markupMappings = markupEmptyMarkupMapping();
      // for each context update the site
      wchForEachRenderingContext(aRenderingContext, rc =>
        markupAddMarkup(rc, markupMappings, aProviders)
      );
      // listen for changes to the template files
      const refs: Observable<string>[] = _createTemplateRefs(
        markupMappings,
        options
      );
      // perform an update
      return rxPipe(
        of('initial'),
        merge(combineLatest(refs)),
        log('onUpdateContext'),
        map(id =>
          _cloneRenderingContext(
            aRenderingContext,
            markupMappings,
            aSiteContext,
            options
          )
        )
      );
    }

    function _createForSites(
      aSiteInformation: SiteInformation,
      aRenderingContexts: RenderingContextQueryResult,
      aProviders: MarkupProviders,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContextQueryResult> {
      // options to use
      const options = aOptions || _httpResourceOptions;
      // remember which site contexts to update
      const markupMappings = markupEmptyMarkupMapping();
      // for each context update the site
      const contexts = aRenderingContexts.renderingContexts;
      let lenContexs = contexts.length;
      while (lenContexs-- > 0) {
        wchForEachRenderingContext(contexts[lenContexs], rc =>
          markupAddMarkup(rc, markupMappings, aProviders)
        );
      }
      // listen for changes to the template files
      const refs: Observable<string>[] = _createTemplateRefs(markupMappings);
      // perform an update
      return of('initial').pipe(
        merge(combineLatest(refs)),
        tap(id => li(LOGGER, 'updating context because of', id)),
        map(id =>
          contexts.map(rc =>
            _cloneRenderingContext(
              rc,
              markupMappings,
              aSiteInformation.getSiteContextById(rc.id),
              options
            )
          )
        ),
        map(cloned => ({
          numFound: aRenderingContexts.numFound,
          renderingContexts: cloned
        }))
      );
    }

    /*
     * Augments the given of rendering context and keeps it up to date
     *
     * @param aContext   the context
     * @param aOptions    the update uptions
     * @return an observable about the updated contexts
     */
    function _createAugmentedRenderingContext(
      aRenderingContext: RenderingContext,
      aProviders: MarkupProviders,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext> {
      // ID
      const contentId = aRenderingContext.id;
      // when the site changes, we need to recompute everything
      return rxPipe(
        _getSiteInformation().site,
        switchMap(site =>
          _createForSite(
            _getSiteInformation().getSiteContextById(contentId),
            aRenderingContext,
            aProviders,
            aOptions
          )
        )
      );
    }

    /*
     * Augments the given array of rendering contexts and keeps them up to date
     *
     * @param aContexts   the contexts
     * @param aOptions    the update uptions
     * @return an observable about the updated contexts
     */
    function _augmentRenderingContexts(
      aRenderingContexts: RenderingContextQueryResult,
      aProviders: MarkupProviders,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContextQueryResult> {
      // when the site changes, we need to recompute everything
      return rxPipe(
        _getSiteInformation().site,
        switchMap(site =>
          _createForSites(
            _getSiteInformation(),
            aRenderingContexts,
            aProviders,
            aOptions
          )
        )
      );
    }

    /*
     * Returns the rendering context given the activated route
     *
     * @param aRoute  the route
     * @param aLevels number of levels to resolve when fetching the context
     *
     * @return an observable with the rendering context
     */
    function _getRenderingContextByActivatedRoute(
      aRoute: ActivatedRoute,
      aLevels?: number
    ): Observable<RenderingContext | null | undefined> {
      // attach to the route
      return rxPipe(
        aRoute.url,
        // map the slug to the actual component selection
        switchMap<UrlSegment[], RenderingContext>(segments =>
          _getRenderingContextByUrlSegments(segments, aLevels)
        )
      );
    }

    /**
     * Recursively build the breadcrumb trail
     *
     * @param aSiteContext  the current size context
     * @param aOptions  the resource options
     *
     * @return the sequence of site contexts with new breadcrumb trails
     */
    function _agumentBreadcrumbTrail(
      aSiteContext: SiteContext,
      aOptions?: HttpResourceOptions
    ): Observable<SiteContext> {
      // log the breadcrumb
      li(
        LOGGER,
        '_agumentBreadcrumbTrail',
        aSiteContext.breadcrumb.map(page => page.id)
      );
      // the site
      const siteId = aSiteContext.site.id;
      // check if the top level object in the trail still has a parent
      const breadcrumb = aSiteContext.breadcrumb;
      const firstPage: SitePage =
        breadcrumb && breadcrumb.length > 0 ? breadcrumb[0] : undefined;
      // do we have a parent for this beast
      if (firstPage && firstPage.parentId) {
        // fetch the parent
        return _getParentPageUrl(firstPage.parentId, siteId).pipe(
          switchMap<string, SitePage[]>(url =>
            _getSearchDocuments<SitePage>(url, aOptions)
          ),
          map<SitePage[], SitePage>(
            sitePages =>
              sitePages && sitePages.length > 0 ? sitePages[0] : undefined
          ),
          distinctUntilChanged(pageEquals),
          switchMap<SitePage, SiteContext>(sitePage => {
            // check if we have a result, else the breadcrumb is complete
            if (isNotNil(sitePage)) {
              // create a new context
              const newContext: SiteContext = assign({}, aSiteContext);
              newContext.breadcrumb = [
                pageCloneSitePage(sitePage),
                ...aSiteContext.breadcrumb
              ];
              // freeze the beast to be safe
              const frozenContext: SiteContext = freezeDeep(newContext);
              // recurse
              return _agumentBreadcrumbTrail(frozenContext, aOptions).pipe(
                startWith(frozenContext)
              );
            }
            // nothing
            return empty();
          })
        );
      }
      // nothing to resolve
      return empty();
    }

    /**
     * Returns a list of self-augmenting site contexts for this config
     *
     * @param aSitePage the page to get the context for
     * @param aSite the full site
     * @param aOptions the resource options
     *
     * @return the stream of contexts
     */
    function _getSiteContextForSite(
      aSitePage: SitePage,
      aSite: Site,
      aOptions?: HttpResourceOptions
    ): Observable<SiteContext> {
      // get some initial context
      const partialContext: SiteContext = pageCreatePartialSiteContextForSitePage(
        aSitePage,
        aSite
      );
      // resolve the real context
      return rxPipe(
        _getSiteContextUrl(aSitePage.parentId, aSitePage.id, aSite.id),
        switchMap<string, SitePage[]>(url =>
          _getSearchDocuments<SitePage>(url, aOptions)
        ),
        map<SitePage[], SiteContext>(sitePages =>
          pageCreateSiteContextFromSearchResult(aSitePage, sitePages, aSite)
        ),
        distinctUntilChanged(siteContextEquals),
        switchMap<SiteContext, SiteContext>(siteContext =>
          rxPipe(
            _agumentBreadcrumbTrail(siteContext, aOptions),
            startWith(siteContext)
          )
        ),
        startWith(partialContext),
        log('SiteContextForSite')
      );
    }

    /**
     * Builds a sequence of site contexts
     *
     * @param aSitePage   the page
     * @return the stream of contexts
     */
    function _getSiteContext(
      aSitePage: SitePage,
      aOptions?: HttpResourceOptions
    ): Observable<SiteContext> {
      // construct the context
      return rxPipe(
        _getSiteInformation().site,
        switchMap<Site, SiteContext>(site =>
          _getSiteContextForSite(aSitePage, site, aOptions)
        )
      );
    }

    /**
     * Constructs a rendering context given the site page
     *
     * @param aSitePage   the site page
     * @param aLevels   the levels
     * @return the context
     */
    function _getRenderingContextBySiteContext(
      aContentId: string,
      aOnSiteContext: Observable<SiteContext>,
      aLevels?: number,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext | null | undefined> {
      // method logger
      const LOG_METHOD = '_getRenderingContextBySiteContext';
      // construct the context
      const onSiteContext: Observable<SiteContext> = aOnSiteContext;
      // construct the URL
      const onRenderingContextUrl = _getRenderingContextURL(
        aContentId,
        aLevels
      );
      // some timing
      const tStart = Date.now();
      // make the request
      return rxPipe(
        onRenderingContextUrl,
        // log this
        tap(url =>
          li(
            LOGGER,
            LOG_METHOD,
            'ID',
            aContentId,
            'levels',
            aLevels,
            'URL',
            url
          )
        ),
        // fetch the actual resource
        switchMap<string, RenderingContext>(url =>
          _getJsonResource<RenderingContext>(url, aOptions)
        ),
        // intercept
        _interceptors().opRenderingContext,
        // augment
        switchMap<RenderingContext, RenderingContext>(rc =>
          rxPipe(
            onSiteContext,
            switchMap<SiteContext, RenderingContext>(siteContext =>
              _createForSite(
                siteContext,
                rc,
                markupProviderService.markupProviders
              )
            )
          )
        ),
        // log this
        tap(() =>
          li(LOGGER, LOG_METHOD, aContentId, Date.now() - tStart, '[ms].')
        )
      );
    }

    /**
     * Constructs a rendering context given the site page
     *
     * @param aSitePage   the site page
     * @param aLevels   the levels
     * @return the context
     */
    function _getRenderingContextBySitePage(
      aSitePage: SitePage,
      aLevels?: number,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext | null | undefined> {
      // method logger
      const LOG_METHOD = '_getRenderingContextBySitePage';
      // the ID
      const contentId = aSitePage.contentId;
      // construct the context
      const onSiteContext: Observable<SiteContext> = _getSiteContext(
        aSitePage,
        aOptions
      );
      // dispatch
      return _getRenderingContextBySiteContext(
        contentId,
        onSiteContext,
        aLevels,
        aOptions
      );
    }

    /**
     * Invoked if a route cannot be resolved. We will not conduct a search for a dynamic route
     *
     * @param aPath the escaped path
     * @param aLevels the requested levels
     * @return the observable of the rendering context
     */
    function _getRenderingContextForDynamicPageByPath(
      aPath: string,
      aLevels?: number,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext | null | undefined> {
      // resolve the beast
      return rxPipe(
        _getSiteInformation().site,
        map<Site, string>(site => site.id),
        opDistinctUntilChanged,
        switchMap<string, string>(siteId =>
          _getPageSearchUrl(pathForSearch(aPath), siteId)
        ),
        switchMap<string, SitePage[]>(url =>
          _getSearchDocuments<SitePage>(url, aOptions)
        ),
        switchMap<SitePage[], RenderingContext>(
          sitePages =>
            sitePages.length > 0
              ? _getRenderingContextBySitePage(sitePages[0], aLevels, aOptions)
              : _getRenderingContextById(null, aLevels, aOptions)
        )
      );
    }

    /*
     * Returns the rendering context given the path
     *
     * @param aPath  the path
     * @param aLevels number of levels to resolve when fetching the context
     *
     * @return an observable with the rendering context
     */
    function _getRenderingContextByPath(
      aPath: string,
      aLevels?: number,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext | null | undefined> {
      // map the slug to the actual component selection
      return rxPipe(
        _getIdByPath(aPath),
        // access the rendering context
        switchMap<string, RenderingContext>(
          id =>
            id === null
              ? _getRenderingContextForDynamicPageByPath(
                  aPath,
                  aLevels,
                  aOptions
                )
              : _getRenderingContextById(id, aLevels, aOptions)
        )
      );
    }

    /*
     * Returns the rendering context given the path
     *
     * @param aPage  the page
     * @param aLevels number of levels to resolve when fetching the context
     *
     * @return an observable with the rendering context
     */
    function _getRenderingContextByPage(
      aSitePage: SitePage,
      aLevels?: number,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext | null | undefined> {
      // log this
      li(LOGGER, '_getRenderingContextByPage', aSitePage, aLevels);
      // check if we know the site context for the page
      const pageId = aSitePage.id;
      const contentId = aSitePage.contentId;
      const siteContext = _getSiteInformation().getSiteContextById(pageId);
      // if the context is known, ideal
      if (isNotNil(siteContext) && isNotNil(siteContext.site)) {
        // return with this context
        return _getRenderingContextBySiteContext(
          contentId,
          of(siteContext),
          aLevels,
          aOptions
        );
      }
      // this is a dynamic page, build the context from the page
      return _getRenderingContextBySitePage(aSitePage, aLevels, aOptions);
    }

    /*
     * Returns the rendering context given the URL segments.
     *
     * @param aSegments  the segments
     * @param aLevels number of levels to resolve when fetching the context
     *
     * @return an observable with the rendering context
     */
    function _getRenderingContextByUrlSegments(
      aSegments: UrlSegment[],
      aLevels?: number,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext | null | undefined> {
      // map the slug to the actual component selection
      return rxPipe(
        // get from the segments
        _getIdByUrlSegments(aSegments),
        // access the rendering context
        switchMap<string, RenderingContext>(
          id =>
            id === null
              ? _getRenderingContextForDynamicPageByPath(
                  pathGetPathFromUrlSegments(aSegments),
                  aLevels,
                  aOptions
                )
              : _getRenderingContextById(id, aLevels, aOptions)
        )
      );
    }

    /*
     * Returns the rendering context given an element ID. If the ID could not be resolved, then the
     * context will be undefined if the information is unknown to exist, yet, or null if the information
     * is known not to exist.
     *
     * @param aID  the ID
     * @param aLevels number of levels to resolve when fetching the context
     *
     * @return an observable with the rendering context
     */
    function _getRenderingContextById(
      aID: string | null | undefined,
      aLevels?: number,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext | null | undefined> {
      // only process for valid IDs
      if (aID === undefined) {
        // returns the undefined context
        return UNKNOWN_RENDERING_CONTEXT;
      }
      if (aID === null) {
        // returns the null context
        return rxPipe(
          NO_RENDERING_CONTEXT,
          // intercept
          _interceptors().opRenderingContext,
          // augment
          switchMap<RenderingContext, RenderingContext>(rc =>
            _createAugmentedRenderingContext(
              rc,
              markupProviderService.markupProviders
            )
          )
        );
      }
      // construct the URL
      const onRenderingContextUrl = _getRenderingContextURL(aID, aLevels);
      // some timing
      const tStart = Date.now();
      // make the request
      return rxPipe(
        onRenderingContextUrl,
        // log this
        tap(url =>
          li(
            LOGGER,
            'getRenderingContextById',
            'ID',
            aID,
            'levels',
            aLevels,
            'URL',
            url
          )
        ),
        // fetch the actual resource
        switchMap(url => _getJsonResource<RenderingContext>(url, aOptions)),
        // intercept
        _interceptors().opRenderingContext,
        // augment
        switchMap(rc =>
          _createAugmentedRenderingContext(
            rc,
            markupProviderService.markupProviders
          )
        ),
        // log this
        tap(() =>
          li(
            LOGGER,
            'getRenderingContextById',
            aID,
            Date.now() - tStart,
            '[ms].'
          )
        )
      );
    }

    /*
     * Converts a sequence of URL segments into an ID. If the segments could not be mapped
     * to an ID, then the observable will contain undefined if this is because the site has not been loade, yet or
     * null if the page could not be found.
     *
     * @param aSegments   the segments
     */
    function _getIdByUrlSegments(
      aSegments: UrlSegment[]
    ): Observable<string | null | undefined> {
      // convert the segments
      const path = pathGetPathFromUrlSegments(aSegments);
      // decode
      return (
        _getSiteInformation()
          // get the node from path
          .getIdByPath(path)
          .pipe(
            // just some logging
            tap(id =>
              li(
                LOGGER,
                'getIdByUrlSegments',
                'Segments:',
                '[',
                aSegments,
                ']',
                'Result:',
                '[',
                id,
                ']'
              )
            )
          )
      );
    }

    /*
   * Converts a sequence of URL segments into an ID. If the segments could not be mapped
   * to an ID, then the observable will contain undefined if this is because the site has not been loaded, yet or
   * null if the page could not be found.
   *
   * @param aSegments   the segments
   */
    function _getIdByPath(
      aPath: string
    ): Observable<string | null | undefined> {
      // decode
      return (
        _getSiteInformation()
          // get the node from path
          .getIdByPath(aPath)
          .pipe(
            // just some logging
            tap(id => li(LOGGER, 'getIdByPath', aPath, id))
          )
      );
    }

    function _getRenderingContextForAppConfig(
      aLevels?: number,
      aOptions?: HttpResourceOptions
    ): Observable<RenderingContext> {
      // listen for changes in the app config
      return rxPipe(
        _getAppConfig(),
        switchMap<ApplicationConfig, RenderingContext>(cfg =>
          _getRenderingContextById(cfg.id, aLevels, aOptions)
        )
      );
    }

    /*
    * Executes the query and returns the rendering contexts.
    *
    * @param aQuery the query string
    * @param aLevels the levels to fetch
    *
    * @return  an observable with the rendering results
    */
    function _getRenderingContextsByQuery(
      aQuery: string,
      aLevels?: number
    ): Observable<RenderingContextQueryResult> {
      // only process for valid queries
      if (isNotNil(aQuery)) {
        // construct the URL
        const onQueryUrl = _getQueryURL(
          wchAdjustRenderingContextSearchQuery(aQuery)
        );
        // make the request
        return rxPipe(
          onQueryUrl,
          // perform the query against the given URL
          switchMap<string, SearchResults<RenderingContextSearchResult>>(url =>
            _getJsonResource<SearchResults<RenderingContextSearchResult>>(url)
          ),
          // returns a list of documents
          map<
            SearchResults<RenderingContextSearchResult>,
            RenderingContextQueryResult
          >(searchResults => ({
            numFound: searchResults.numFound,
            renderingContexts: (
              searchResults.documents || EMPTY_RENDERING_CONTEXT_SEARCH_RESULT
            ).map(_pluckDocument)
          })),
          // compare semantically to avoid changes
          opDeepDistinctUntilChanged,
          // log this
          tap(rcs => li(LOGGER, 'getRenderingContextsByQuery', aQuery, rcs)),
          // intercept the result set
          switchMap<RenderingContextQueryResult, RenderingContextQueryResult>(
            result =>
              of(result.renderingContexts).pipe(
                _interceptors().opRenderingContexts,
                map<RenderingContext[], RenderingContextQueryResult>(rcs => ({
                  ...result,
                  renderingContexts: rcs
                }))
              )
          ),
          // log this
          tap(rcs =>
            li(
              LOGGER,
              'getRenderingContextsByQuery',
              'intercepted',
              aQuery,
              rcs
            )
          ),
          // augment these contexts
          switchMap<RenderingContextQueryResult, RenderingContextQueryResult>(
            rcs =>
              _augmentRenderingContexts(
                rcs,
                markupProviderService.markupProviders
              )
          ),
          // sanity check
          opAssertInAngularZone()
        );
      } else {
        // nothing
        return EMPTY_RENDERING_CONTEXTS_SEQUENCE;
      }
    }

    /*
    * Executes the query and returns the rendering contexts.
    *
    * @param aQuery the query string
    *
    * @return  an observable with the pages
    */
    function _getSitePagesByQuery(
      aQuery: string
    ): Observable<SitePagesQueryResult> {
      // only process for valid queries
      if (isNotNil(aQuery)) {
        // construct the URL
        return rxPipe(
          _getSiteInformation().site,
          map<Site, string>(_pluckSiteId),
          opDistinctUntilChanged,
          switchMap<string, string>(siteId =>
            wchGetSearchURL(wchAdjustSitePagesSearchQuery(aQuery, siteId))
          ),
          opDistinctUntilChanged,
          switchMap<string, string>(url => _getStringResource(url)),
          map<string, SearchResults<SearchResult<SitePage>>>(jsonParse),
          map<SearchResults<SearchResult<SitePage>>, SitePagesQueryResult>(
            searchResult => ({
              numFound: searchResult.numFound,
              sitePages: (searchResult.documents || [])
                .map(_pluckDocument)
                .map(pageCloneSitePage)
            })
          ),
          // make sure we disallow changes
          map(freezeDeep),
          // compare semantically to avoid changes
          opDeepDistinctUntilChanged
        );
      } else {
        // nothing
        return EMPTY_SITE_PAGES_SEQUENCE;
      }
    }

    /** Access the site information */
    function _getSiteInformation(): SiteInformation {
      // lazily subscribe
      if (isNil(_siteSubscription)) {
        /** observable for the site */
        const _onSite: Observable<Site> = rxPipe(
          wchGetSiteURL(),
          switchMap(unary(_getJsonResource)),
          map(json => json as Site)
        );
        // subscribe
        _siteSubscription = _onSite.subscribe(_siteInfo.observer);
      }
      // returns the value
      return _siteInfo;
    }

    function _getRenderingContextURL(
      aID: string,
      aLevels?: number
    ): Observable<string> {
      // the base URL
      const onRenderingContextURL = wchGetRenderingContextURL(aID);
      const levels = wchBoxFetchLevels(aLevels, _fetchLevels);
      // check if we need to add fetch levels
      return rxPipe(
        onRenderingContextURL,
        map(url => (levels < 0 ? url : `${url}?levels=${levels}`))
      );
    }

    function _getPageSearchUrl(
      aPath: string,
      aSiteId: string
    ): Observable<string> {
      // the base URL
      return wchGetPageSearchURL(aPath, aSiteId);
    }

    function _getSiteContextUrl(
      aParentId: string | null | undefined,
      aPageId: string,
      aSiteId: string
    ): Observable<string> {
      // the base URL
      return wchGetSiteContextURL(aParentId, aPageId, aSiteId);
    }

    function _getParentPageUrl(
      aParentId: string,
      aSiteId: string
    ): Observable<string> {
      // the base URL
      return wchGetParentPageURL(aParentId, aSiteId);
    }

    function _getQueryURL(aQuery: string): Observable<string> {
      return rxPipe(BFF_BASE_URL, map(baseUrl => `${baseUrl}search?${aQuery}`));
    }

    function _getCorsWhitelistURL(): Observable<string> {
      return rxPipe(BFF_BASE_URL, map(baseUrl => `${baseUrl}cors`));
    }

    function _getCorsWhitelist(): Observable<string[]> {
      return rxPipe(
        _getCorsWhitelistURL(),
        switchMap(url => _getJsonHttpResource(url, _httpResourceOptions)),
        filterTypeOf(isStringArray)
      );
    }

    /* Resolves the rendering context according to the configured strategy */
    function _resolveRenderingContext(
      aRenderingContext?: RenderingContext,
      aStrategy?: CYCLE_HANDLING
    ): Observable<RenderingContext | null | undefined> {
      // sanity check
      return wchResolveRenderingContext(
        aRenderingContext,
        aStrategy || _cycleHandlingStrategy,
        _getRenderingContextById
      );
    }

    // some quick accessors
    const _getApiUrl = constGenerator(_apiUrl);
    const _getDeliveryUrl = constGenerator(_deliveryUrl);
    const _getBaseUrl = constGenerator(aUrlService.baseUrl);
    const _getPreviewMode = constGenerator(_isPreviewMode);
    const _getDeliveryOrigin = constGenerator(_deliverOrigin);
    const _getAppConfig = constGenerator(_appConfig);

    that.getAppConfig = _getAppConfig;
    that.resolveRenderingContext = _resolveRenderingContext;
    that.getRenderingContextById = _getRenderingContextById;
    that.getRenderingContextByUrlSegments = _getRenderingContextByUrlSegments;
    that.getRenderingContextByPath = _getRenderingContextByPath;
    that.getRenderingContextByActivatedRoute = _getRenderingContextByActivatedRoute;
    that.getRenderingContextForAppConfig = _getRenderingContextForAppConfig;
    that.getRenderingContextsByQuery = _getRenderingContextsByQuery;
    that.getRenderingContextByPage = _getRenderingContextByPage;
    that.getSitePagesByQuery = _getSitePagesByQuery;
    that.createPollingTrigger = _createPollingTrigger;
    that.getCorsWhitelist = _getCorsWhitelist;
    that.triggerRefresh = (): void => _updateTrigger.next(new Date());
    that.ngOnDestroy = (): void => safeUnsubscribe(_siteSubscription);
    that.getDeliverOrigin = _getDeliveryOrigin;
    that.isPreviewMode = _getPreviewMode;
    that.getApiUrl = _getApiUrl;
    that.getDeliveryUrl = _getDeliveryUrl;
    that.getBaseUrl = _getBaseUrl;
    that.getJsonResource = _getJsonResource;
    that.getTextResource = _getStringResource;

    // done
    perfConstructor();
  }
}
